// The portal's calls to /api/account/balance, /api/account/wallet-entries and the top-ups (features/billing):
// shapes from packages/contracts, the same the v3 API answers with.
import { BalanceResponse, WalletEntryListResponse, type WalletEntryKind } from "@kilat/contracts/wallet";
import { Money } from "@kilat/contracts/money";
import {
  TopUpListResponse,
  TopUpMethodsResponse,
  TopUpResponse,
  type RequestTopUpRequest,
  type TopUpListState,
} from "@kilat/contracts/top-up";
import { z } from "zod";
import { api } from "@/lib/api-client";

export const ENTRIES_PER_PAGE = 25;

/** What the history is narrowed to: a kind, and WIB calendar days, both ends inclusive. */
export type EntryFilters = { kind?: WalletEntryKind | undefined; from?: string | undefined; to?: string | undefined };

/** The list's query string, the page first and only the filters that are set. */
export function entriesSearch(page: number, perPage: number, { kind, from, to }: EntryFilters) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (kind) params.set("kind", kind);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  return params.toString();
}

export const getBalance = () => api.request("/api/account/balance", { schema: BalanceResponse });

export const listWalletEntries = (page: number, filters: EntryFilters) =>
  api.request(`/api/account/wallet-entries?${entriesSearch(page, ENTRIES_PER_PAGE, filters)}`, {
    schema: WalletEntryListResponse,
  });

// Top-up (docs/plans/billing-top-up.md §7): the channels and limits, the reseller's top-ups, and the
// fake gateway's hosted page, which exists only where GATEWAY_PROVIDER is fake (never production).
export const TOP_UPS_PER_PAGE = 25;

export const getTopUpMethods = () => api.request("/api/account/top-up-methods", { schema: TopUpMethodsResponse });

/** What the top-ups tab is narrowed to: a state (in_review for both dispute states), and WIB days. */
export type TopUpFilters = {
  state?: TopUpListState | undefined;
  from?: string | undefined;
  to?: string | undefined;
};

export function listTopUps(page: number, { state, from, to }: TopUpFilters = {}) {
  const params = new URLSearchParams({ page: String(page), per_page: String(TOP_UPS_PER_PAGE) });
  if (state) params.set("state", state);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  return api.request(`/api/account/top-ups?${params}`, { schema: TopUpListResponse });
}

export const requestTopUp = (body: RequestTopUpRequest) =>
  api.request("/api/account/top-ups", { method: "POST", body, schema: TopUpResponse });

export const cancelTopUp = (topUpId: string) =>
  api.request(`/api/account/top-ups/${topUpId}/cancel`, { method: "POST", schema: TopUpResponse });

const FakePaymentResponse = z.object({
  data: z.object({ reference: z.string(), charged: Money, status: z.string(), expires_at: z.iso.datetime() }),
});

const fakePath = (reference: string) => `/api/fake-gateway/payments/${encodeURIComponent(reference)}`;

export const getFakePayment = (reference: string) => api.request(fakePath(reference), { schema: FakePaymentResponse });

export const settleFakePayment = (reference: string, action: "pay" | "fail") =>
  api.request(`${fakePath(reference)}/${action}`, { method: "POST" });
